<template>
    <div>
        <gmap-map :center="{lat:mapCenter.lat, lng:mapCenter.lng}"
                  :map-type-id="mapType"
                  :options="mapOptions"
                  :zoom="mapZoomLevel"
                  @bounds_changed="onBoundsChange($event)"
                  @center_changed="onCenterChange($event)"
                  @zoom_changed="onZoomChange($event)"
                  style="width: 100%; height: 92vh">

            <gmap-marker :position="myUserPosition"
                         :animation="2"
                         :icon="myMarkerIcon()"></gmap-marker>

            <gmap-marker v-for="survey in filteredMarkerSurveys" :key="survey.uuid"
                         :position="survey.shape.position"
                         :clickable="true"
                         :icon="mapMarkerIcon(survey)"
                         @click="onContextShow(survey)"></gmap-marker>

            <gmap-circle v-for="survey in filteredCircleSurveys" :key="survey.uuid"
                         :center="survey.shape.position"
                         :radius="survey.shape.radius"
                         :draggable="false"
                         :editable="false"
                         :clickable="true"
                         :options="{strokeColor: (plantRef(survey) ? plantRef(survey).color : 'black'), fillColor: (plantRef(survey) ? plantRef(survey).color : 'black')}"
                         @click="onContextShow(survey)"></gmap-circle>

            <gmap-polygon v-for="survey in filteredPolygonSurveys" :key="survey.uuid"
                          :paths="survey.shape.paths"
                          :draggable="false"
                          :editable="false"
                          :clickable="true"
                          :options="{strokeColor: (plantRef(survey) ? plantRef(survey).color : 'black'), fillColor: (plantRef(survey) ? plantRef(survey).color : 'black')}"
                          @click="onContextShow(survey)"></gmap-polygon>
        </gmap-map>
        <MapToolbar @plants-change="onPlantsChange" @map-type-change="onMapTypeChange"></MapToolbar>
        <MapSearch @setZoom="mapZoomLevel=$event"></MapSearch>
        <MapTimeline v-on:range-change="onRangeChange"></MapTimeline>
        <v-navigation-drawer v-model="showContext" right temporary fixed :width="400">
            <map-context v-on:context-close="onContextClose"></map-context>
        </v-navigation-drawer>

    </div>
</template>

<script>
    import _ from 'lodash'
    import {MapTypes} from '../../_helpers';
    import MapContext from './MapContext';
    import MapToolbar from './MapToolbar';
    import MapTimeline from './MapTimeline';
    import MapSearch from "./Map Search";

    const ZOOM_LEVEL_DEFAULT = 12
    const ZOOM_LEVEL_THRESHOLD = 15

    export default {
        name: 'MapHome',
        components: {
            MapSearch,
            MapContext,
            MapToolbar,
            MapTimeline
        },
        computed: {
            authenticated() {
                let status = this.$store.getters.isAuthenticated
                // Update options of map
                this.setMapOptionsMaxZoom(status ? null : ZOOM_LEVEL_DEFAULT)
                return status
            },
            myUserPosition() {
                return this.$store.getters.userPosition
            },
            mapLocationDefault() {
                return this.$store.getters.mapLocationDefault
            },
            // copied from MapEditor
            mapCenter() {
                // Get retrieved location
                let location = this.$store.getters.mapLocation

                // Read last stored location from local-storage (fallback)
                if (!location) {
                    if(typeof(localStorage.mapCenter) == 'undefined') {
                        location = {lat: this.mapLocationDefault.lat, lng: this.mapLocationDefault.lng}
                    }
                    else {
                        location = JSON.parse(localStorage.mapCenter)
                    }
                }
                return location
            },
            filteredMarkerSurveys() {
                if (this.mapZoomLevel < ZOOM_LEVEL_THRESHOLD) {
                    return this.$store.getters.filteredSurveyList
                }
                return this.$store.getters.filteredSurveyList.filter(survey => survey.shape.type === 'marker')
            },
            filteredCircleSurveys() {
                if (this.mapZoomLevel < ZOOM_LEVEL_THRESHOLD) {
                    return []
                }
                return this.$store.getters.filteredSurveyList.filter(survey => survey.shape.type === 'circle')
            },
            filteredPolygonSurveys() {
                if (this.mapZoomLevel < ZOOM_LEVEL_THRESHOLD) {
                    return []
                }
                return this.$store.getters.filteredSurveyList.filter(survey => survey.shape.type === 'polygon')
            }
        },
        data: () => ({
            mapType: null,
            mapZoomLevel: ZOOM_LEVEL_DEFAULT,
            showContext: false,
            filterPlantIds: [],
            filterToDate: null,

            // Details see https://developers.google.com/maps/documentation/javascript/reference/map#MapOptions
            mapOptions: {
                zoomControl: false,
                mapTypeControl: false,
                scaleControl: false,
                streetViewControl: false,
                rotateControl: false,
                fullscreenControl: false,
                disableDefaultUi: false,
                maxZoom: ZOOM_LEVEL_DEFAULT,
                styles : [
                    {
                        "featureType": "administrative",
                        "elementType": "geometry.stroke",
                        "stylers": [
                            {
                                "color": "#ff3c41"
                            },
                            {
                                "weight": 2.5
                            }
                        ]
                    },
                    {
                        featureType: 'poi',
                        elementType: 'labels.icon',
                        stylers: [{
                            visibility: 'off'
                        }]
                    }
                ]
            }
        }),
        mounted() {
            this.$store.dispatch('getAllSurveys');

            // Initialize data from local-storage
            this.mapType = (localStorage.mapType) ? localStorage.mapType : MapTypes.HYBRID.value
        },
        watch: {
            mapType(newMapType) {
                localStorage.mapType = newMapType
            },
            // Necessary, otherwise code in computed won't work?
            authenticated(newState, oldState) {
                console.log(`Authenticated changed: ${oldState} to ${newState}`)
                //this.setMapOptionsMaxZoom(newState ? null : ZOOM_LEVEL_DEFAULT)
            }
        },
        methods: {
            setMapOptionsMaxZoom(zoomLevel) {
                this.mapOptions.maxZoom = zoomLevel
            },
            myMarkerIcon() {
                return {
                    path: "M12,2C15.31,2 18,4.66 18,7.95C18,12.41 12,19 12,19C12,19 6,12.41 6,7.95C6,4.66 8.69,2 12,2M12,6A2,2 0 0,0 10,8A2,2 0 0,0 12,10A2,2 0 0,0 14,8A2,2 0 0,0 12,6M20,19C20,21.21 16.42,23 12,23C7.58,23 4,21.21 4,19C4,17.71 5.22,16.56 7.11,15.83L7.75,16.74C6.67,17.19 6,17.81 6,18.5C6,19.88 8.69,21 12,21C15.31,21 18,19.88 18,18.5C18,17.81 17.33,17.19 16.25,16.74L16.89,15.83C18.78,16.56 20,17.71 20,19Z",
                    anchor: {x: 13, y: 20},
                    fillColor: "green",
                    fillOpacity: 1,
                    scale: 1.5,
                    strokeColor: "white",
                    strokeWeight: 2
                }
            },
            mapMarkerIcon(survey) {
                // Search for referenced plant
                let plant = this.$store.state.plant.plantList.find(plant => plant.uuid === survey.neophyte)

                // Create a SVG map-marker; properties are defined at
                // https://developers.google.com/maps/documentation/javascript/reference/marker#Icon
                return {
                    path: "M12,11.5A2.5,2.5 0 0,1 9.5,9A2.5,2.5 0 0,1 12,6.5A2.5,2.5 0 0,1 14.5,9A2.5,2.5 0 0,1 12,11.5M12,2A7,7 0 0,0 5,9C5,14.25 12,22 12,22C12,22 19,14.25 19,9A7,7 0 0,0 12,2Z",
                    anchor: {x: 13, y: 20},
                    fillColor: plant.color,
                    fillOpacity: 1,
                    scale: 1.5,
                    strokeColor: "black",
                    strokeWeight: 1
                }
            },
            plantRef(survey) {
                return this.$store.getters.plantList.find(plant => plant.uuid === survey.neophyte)
            },
            onBoundsChange: _.debounce(function(event) {
                console.log(`Bounds changed: ${event}`)
            }, 500),
            onCenterChange: _.debounce(function(event) {
                console.log(`Center changed: ${event}`)

                // Store last position to local-storage
                localStorage.mapCenter = JSON.stringify(event)
                // Add commit to save new center to state
                this.$store.commit('LOCATION', {
                    latitude: event.lat(),
                    longitude: event.lng()
                })
            }, 500),
            onZoomChange: _.debounce(function(zoomLevel) {
                console.log(`Zoom changed: ${zoomLevel}`)
                // Update property
                this.mapZoomLevel = zoomLevel
            }, 500),
            onPlantsChange(plantIds) {
                this.filterPlantIds = plantIds

                let filterArgs = {
                    plantIds: this.filterPlantIds,
                    toDate: this.filterToDate
                }

                this.$store.dispatch('filterSurveys', filterArgs)
            },
            onMapTypeChange(mapType) {
                this.log(`Map type changed: ${mapType}`)
                this.mapType = mapType
            },
            onRangeChange(toDate) {
                this.log(`Range changed to: ${toDate}`)
                this.filterToDate = toDate

                let filterArgs = {
                    plantIds: this.filterPlantIds,
                    toDate: this.filterToDate
                }

                this.$store.dispatch('filterSurveys', filterArgs)
            },
            onContextShow(survey) {
                this.log(`Clicked on marker: ${JSON.stringify(survey)}`)

                this.$store.dispatch('showMapContext', survey)

                this.showContext = survey !== null;
            },
            onContextClose() {
                this.showContext = false
            },
            log(text) {
                console.log(`[MapHome] ${text}`)
            }
        }
    };
</script>

<style>

</style>
